<template>
  <b-container id="summary-container" fluid>
    <b-row>
      <b-col class="px-3 pb-5 px-6-md pb-2-md shadow bg-white" offset-xl="2" xl="8">
        <b-row class="d-flex justify-content-between py-3">
          <b-col cols="12" md="6" :class="`${printSummaryClass} mb-3`">
            <h2 v-if="active || closed || scheduled">FON{{scheduled ? '' : ` #${notification.fonNumber}`}} Summary</h2>
            <h2 v-else-if="draft || pendingCertification">FON Final Review</h2>
            <h2 v-else>FON Summary</h2>
            <span>Status: {{ getStatusName(notification.statusId) }}</span>
          </b-col>
          <div style="margin-left:15px;" v-if="scheduled || isOperationStartDateFuture">The number for this notification will not display for you before the start date of {{formattedOperationStartDate}}.</div>
          <b-col cols="12" md="6" class="d-flex justify-content-end" :class="isExtraSmall ? 'flex-dir-col' : ''">
            <div
              v-if="pendingCertification && (mfsAdmin || notifierUser)"
              class="hide-on-print w-100 w-md-auto">
              <WithdrawModal
                class="mb-2 mr-3"
                :modalDisabled="false"
              />
            </div>
            <div
              v-if="pendingCertification && certificationRequired"
              class="hide-on-print w-100 w-md-auto">
              <DeclineModal
                class="mb-2 mr-3"
                :modalDisabled="false"
                @declined="resetSectionsRead"
              />
            </div>
            <PrintPosting
              :class="`${!showPosting ? 'hide-on-print' : ''} w-100 w-md-auto`"
              v-if="active || closed"
              @printPosting="onPrintPosting"
              @printSummary="onPrintSummary" />
          </b-col>
        </b-row>
        <div v-if="anyErrors" :class="printSummaryClass">
          <b-alert v-if="errors.overviewErrors.length" variant="danger" show>
            Please return to the Overview page and fix the following errors:
            <ul class="mb-0">
              <li v-for="error in errors.overviewErrors" :key="error">
                {{error}}
              </li>
            </ul>
          </b-alert>
          <b-alert v-if="errors.mappingErrors.length" variant="danger" show>
            Please return to the Mapping page and fix the following errors:
            <ul class="mb-0">
              <li v-for="error in errors.mappingErrors" :key="error">
                {{error}}
              </li>
            </ul>
          </b-alert>
          <b-alert v-if="errors.activityDetailsErrors.length" variant="danger" show>
            Please return to the Activity Details page and fix the following errors:
            <ul class="mb-0">
              <li v-for="error in errors.activityDetailsErrors" :key="error">
                {{error}}
              </li>
            </ul>
          </b-alert>
          <b-alert v-if="errors.contactsErrors.length" variant="danger" show>
            Please return to the Contacts page and fix the following errors:
            <ul class="mb-0">
              <li v-for="error in errors.contactsErrors" :key="error">
                {{error}}
              </li>
            </ul>
          </b-alert>
        </div>
        <div :class="printSummaryClass">
          <SummaryCard
            v-if="active || closed || scheduled"
            id="landownerReportsCard"
            title="Landowner Reports"
            scrollToId="landownerReportsCard"
            :collapsible="false"
            v-model="lorRead"
          >
            <template v-slot:title>
              Landowner Reports<span v-if="hasLandownerReportsToComplete"> - REQUIRED</span>
              <div v-if="hasLandownerReportsToComplete" style="font-size:14px;font-weight:300;color:#212529"><br/>
                <div>Now that you have a Forest Operations Notification (FON) you are <a href="https://legislature.maine.gov/statutes/12/title12sec8885.html" target="_blank" style="text-decoration-line: underline;">required by law</a> to report each year your FON is active:</div>
                <ol style="margin-bottom:0">
                  <li>If you had a harvest or not</li>
                  <li>And if you did have a harvest, you are required to report your harvest activities (acreage harvested, tree species sold, etc.)</li>
                </ol>
                <br/>Below are links to the annual landowner reports you must complete:
              </div>
            </template>
            <LandownerReports />
          </SummaryCard>
          <SummaryCard
            v-if="pendingCertification && canSeePendingPartySection"
            id="CertificationPartiesCard"
            title="Delinquent Parties"
            scrollToId="CertificationPartiesCard"
            v-model="certificationsRead"
            :collapsible="false"
          >
            <CertificationParties />
          </SummaryCard>
          <SummaryCard
            v-if="!draft"
            id="certificationsCard"
            title="Certifications"
            scrollToId="certificationsCard"
            v-model="certificationsRead"
            :collapsible="false"
          >
            <Certifications />
          </SummaryCard>
          <SummaryCard
            id="forestOperationsContactsCard"
            title="Forest Operations Contacts"
            scrollToId="forestOperationsContactsCard"
            v-model="sectionsRead.contact"
            :collapsible="certificationRequired"
            context="Contacts"
          >
            <FONContactDetails />
          </SummaryCard>
          <SummaryCard
            id="overviewCard"
            title="Overview"
            scrollToId="overviewCard"
            v-model="sectionsRead.overview"
            :collapsible="certificationRequired"
            context="Overview"
          >
            <OverviewDetails :notification="notification" />
          </SummaryCard>
          <SummaryCard
            id="mapCard"
            title="Map"
            scrollToId="mapCard"
            v-model="sectionsRead.map"
            :collapsible="certificationRequired"
            context="Map"
          >
            <MapDetails ref="mapDetails" />
          </SummaryCard>
          <SummaryCard
            id="activityDetailsCard"
            title="Activity Details"
            scrollToId="activityDetailsCard"
            v-model="sectionsRead.activityDetails"
            :collapsible="certificationRequired"
            context="Activity Details"
          >
            <ActivityDetails />
          </SummaryCard>
          <SummaryCard
            id="mainForestServiceContactsCard"
            title="Maine Forest Service Contacts"
            scrollToId="mainForestServiceContactsCard"
            v-model="sectionsRead.mfsContacts"
            :collapsible="certificationRequired"
            :acknowledgement="true"
            context="Maine Forest Service Contacts"

          >
            <MFSContactDetails :townGeocodeNum="notification.townGeocodeNum" />
          </SummaryCard>
          <SummaryCard
            id="importantRemindersCard"
            title="Important Reminders"
            scrollToId="importantRemindersCard"
            v-model="sectionsRead.importantReminders"
            :collapsible="certificationRequired"
            :acknowledgement="true"
            context="Important Reminders"
          >
            <ImportantReminders ref="importantReminders" />
          </SummaryCard>
          <SummaryCard
            id="amendmentsCard"
            title="Amendments"
            :value="false"
            :collapsible="false"
            v-if="active || closed || scheduled">
            <UploadAmendmentsForm />
          </SummaryCard>
        </div>
        <template v-if="(draft || pendingCertification) && certificationRequired">
          <div class="d-flex flex-column flex-sm-row justify-content-between">
            <div class="my-3 mb-sm-8">
              Please read and certify each section before certifying. To reopen a section,
              uncheck the Certified or Acknowledged box.
            </div>
            <CertifyModal
              class="mt-3 mb-4 mb-sm-8 float-right"
              :modalDisabled="modalDisabled"
              @certified="resetSectionsRead"
            />
          </div>
        </template>
      </b-col>
    </b-row>
    <b-modal
      ref="certifiedModal"
      content-class="px-lg-5"
      id="certified"
      title="Successfully Certified"
      @ok="closeCertifiedModal"
      footer-class="justify-content-center"
      hide-header-close
      centered
    >
      {{certifiedMessage}}
      <template v-slot:modal-footer="{ ok }">
        <b-button
          pill
          variant="primary"
          @click="ok()"
          aria-label="Confirm certification"
        >
          Ok
        </b-button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Vue from 'vue';
import moment from 'moment';
import ActivityDetails from '@/components/notification/summary/ActivityDetails.vue';
import CertifyModal from '@/components/notification/summary/CertifyModal.vue';
import Certifications from '@/components/notification/summary/Certifications.vue';
import CertificationParties from '@/components/notification/summary/CertificationParties.vue';
import DeclineModal from '@/components/notification/summary/DeclineModal.vue';
import FONContactDetails from '@/components/notification/summary/FONContactDetails.vue';
import ImportantReminders from '@/components/notification/summary/ImportantReminders.vue';
import LandownerReports from '@/components/notification/summary/LandownerReports.vue';
import LookupModules from '@/store/moduleConfig';
import MapDetails from '@/components/notification/summary/MapDetails.vue';
import MFSContactDetails from '@/components/notification/summary/MFSContactDetails.vue';
import OverviewDetails from '@/components/notification/summary/OverviewDetails.vue';
import PrintPosting from '@/components/notification/summary/PrintPosting.vue';
import SummaryCard from '@/components/common/SummaryCard.vue';
import WithdrawModal from '@/components/notification/summary/WithdrawModal.vue';
import UploadAmendmentsForm from '@/components/notification/summary/UploadAmendmentsForm.vue';

const initialState = () => ({
  sectionsRead: {
    contact: false,
    overview: false,
    map: false,
    activityDetails: false,
    mfsContacts: false,
    importantReminders: false,
  },
  certificationsRead: false,
  lorRead: false,
  showPosting: false,
});

export default {
  name: 'Summary',
  components: {
    ActivityDetails,
    CertifyModal,
    CertificationParties,
    Certifications,
    DeclineModal,
    FONContactDetails,
    ImportantReminders,
    LandownerReports,
    MapDetails,
    MFSContactDetails,
    OverviewDetails,
    PrintPosting,
    SummaryCard,
    WithdrawModal,
    UploadAmendmentsForm,
  },
  props: {
    fullscreen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return initialState();
  },
  mounted() {
    this.$store.dispatch(`${LookupModules.NOTIFICATION_STATUSES.namespace}/fetchOptions`);
    if (this.pendingCertification || this.active || this.scheduled) this.reload();
  },
  computed: {
    ...mapState(['isExtraSmall']),
    ...mapState('Notification', {
      loading: state => state.loading,
      notification: state => state.notification,
      certifications: state => state.certifications,
      showCertifiedModal: state => state.showCertifiedModal,
      errors: state => state.errors,
      landownerReports: state => state.landownerReports,
    }),
    ...mapState('User', {
      user: state => state.user,
    }),
    ...mapGetters('Notification', [
      'anyErrors',
      'pendingCertification',
      'draft',
      'active',
      'closed',
      'scheduled',
    ]),
    ...mapGetters('User', [
      'mfsUser',
      'tceoUser',
      'infwbUser',
      'mfsAdmin',
    ]),
    ...mapGetters(LookupModules.NOTIFICATION_STATUSES.namespace, {
      getStatusName: 'getName',
    }),
    modalDisabled() {
      if (this.mfsAdmin && !this.anyErrors) {
        return false;
      }
      return !this.allSectionsRead || this.anyErrors;
    },
    allSectionsRead() {
      return Object.values(this.sectionsRead).reduce((acc, sectionRead) => acc && sectionRead);
    },
    certificationRequired() {
      return !this.certifications.some(c => c.user.id === this.user.id && c.declined != null)
        && (this.draft || this.pendingCertification);
    },
    notifierUser() {
      return this.notification.createdBy === this.user.id;
    },
    certifiedMessage() {
      let otherContacts = ' The other Contacts have been notified that the FON is ready for certification.';
      let editable = ' The FON is no longer editable unless you choose to withdraw the FON.';
      let statusText = ' You may continue to view the summary during certification.';
      if (!this.notifierUser && this.pendingCertification) {
        otherContacts = ' The other Contacts must still certify the FON.';
        editable = '';
      }

      if (this.active) {
        otherContacts = '';
        editable = '';
        statusText = ' The FON was submitted to MFS and is now Active.';
      }

      if (this.scheduled) {
        otherContacts = '';
        editable = '';
        statusText = ' The FON was submitted to MFS and is now Scheduled.';
      }

      return `You have successfully certified this FON.${otherContacts}${statusText}${editable}`;
    },
    printSummaryClass() {
      return this.showPosting ? 'hide-on-print' : '';
    },
    canSeePendingPartySection() {
      return this.mfsAdmin || this.mfsUser
        || (this.notification.landownerUser != null && this.notification.landownerUser.id === this.user.id)
        || (this.notification.landownerUser != null && this.notification.landownerUser.id === this.user.id)
        || (this.notification.designatedAgentUser != null && this.notification.designatedAgentUser.id === this.user.id);
    },
    hasLandownerReportsToComplete() {
      let anyReportsToComplete = false;
      if (this.landownerReports && this.landownerReports.length > 0) {
        for (let i = 0; i < this.landownerReports.length; i++) {
          const lor = this.landownerReports[i];
          if ((lor.id === 0 && lor.notRequired === false) || (lor.id !== 0 && lor.statusId !== 2 && lor.statusId !== 4)) {
            anyReportsToComplete = true;
          }
        }
      }
      return anyReportsToComplete;
    },
    formattedOperationStartDate() {
      return this.notification.operationStartDate
        ? moment(this.notification.operationStartDate).format('MM/DD/YYYY')
        : '';
    },
    isOperationStartDateFuture() {
      return this.notification.operationStartDate
        ? moment(this.notification.operationStartDate).isAfter(moment(), 'day')
        : false;
    },
  },
  watch: {
    showCertifiedModal(value) {
      if (value) this.showModal();
      else this.hideModal();
    },
  },
  methods: {
    resetSectionsRead() {
      Object.assign(this.$data, initialState());
    },
    reload() {
      this.resetSectionsRead();
      this.$store.dispatch('Notification/fetchCertifications');
      this.$store.dispatch('Notification/validate');
      this.$store.dispatch('Notification/fetchRoles');
      this.$store.dispatch('ActivityDetails/forceFetch');
      this.$store.dispatch('ActivityDetails/fetchRequirements');
      this.$refs.mapDetails.reloadMap();
      this.$refs.importantReminders.reload();
    },
    async submitWrapper() {
      return true;
    },
    showModal() {
      this.$refs.certifiedModal.show();
    },
    hideModal() {
      this.$refs.certifiedModal.hide();
    },
    closeCertifiedModal() {
      this.$store.dispatch('Notification/hideCertifiedModal');
    },
    onPrintPosting() {
      this.showPosting = true;
      Vue.nextTick(() => window.print());
    },
    onPrintSummary() {
      this.showPosting = false;
      Vue.nextTick(() => window.print());
    },
  },
};
</script>
<style>
.flex-dir-col{
  flex-direction: column !important;
}
</style>
